import { z } from "zod";
import { lazySchema } from "@repo/lazy-schema";

/** Fixed commissioning profile. Assets outside this universe are preserved. */
export const RestrictedRebalanceScopeSchema = lazySchema(() =>
  z
    .object({
      version: z.literal(1),
      kind: z.literal("base_weth_usdc"),
      outOfScopePolicy: z.literal("preserve"),
      minimumUsdcReserveRaw: z.literal("500000"),
      maximumGrossSellUsd: z.literal("10"),
      slippageBps: z.literal(50),
    })
    .strict(),
);

export type RestrictedRebalanceScope = z.infer<
  typeof RestrictedRebalanceScopeSchema
>;

export const BASE_WETH_USDC_EXECUTION_SCOPE: RestrictedRebalanceScope = {
  version: 1,
  kind: "base_weth_usdc",
  outOfScopePolicy: "preserve",
  minimumUsdcReserveRaw: "500000",
  maximumGrossSellUsd: "10",
  slippageBps: 50,
};

export const BASE_WETH_USDC_ASSET_IDS = [
  "eip155:8453/erc20:0x4200000000000000000000000000000000000006",
  "eip155:8453/erc20:0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
] as const;

export const RESTRICTED_REBALANCE_SCOPE_HASH_DOMAIN =
  "glider-restricted-rebalance-scope-v1";

/** Same sorted-key JSON hashing contract used by execution snapshot identity. */
export async function hashRestrictedRebalanceScope(
  scope: RestrictedRebalanceScope,
): Promise<string> {
  const validated = RestrictedRebalanceScopeSchema.parse(scope);
  const canonical = JSON.stringify(
    Object.fromEntries(
      Object.entries(validated).sort(([left], [right]) =>
        left < right ? -1 : left > right ? 1 : 0,
      ),
    ),
  );
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      `${RESTRICTED_REBALANCE_SCOPE_HASH_DOMAIN}\n${canonical}`,
    ),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
